import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Shield, Zap, HelpCircle, ArrowRight, Award } from "lucide-react";
import { toast } from "sonner";
import { type PlanWithFeatures } from "@/lib/services/plans";
import { usePlansWithFeatures } from "@/hooks/use-plans-data";

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: plans, isLoading, error } = usePlansWithFeatures();
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [planType, setPlanType] = useState<"monthly" | "annual">("monthly");

  const promoParam = searchParams.get("promo") || searchParams.get("ref") || "";
  const isWarRoomPromo =
    promoParam.toUpperCase() === "WARROOM20" ||
    (typeof window !== "undefined" &&
      (localStorage.getItem("mia_promo_code") === "WARROOM20" ||
        localStorage.getItem("mia_referral_code") === "WARROOM20"));

  useEffect(() => {
    if (error) {
      console.error("Error loading plans:", error);
      toast.error("Error al cargar los planes");
    }
  }, [error]);

  const handleStartTrial = async (planId: string) => {
    if (!user) {
      const warRoomQuery = isWarRoomPromo ? `&ref=WARROOM20&promo=WARROOM20` : "";
      navigate(`/signup?trial=true&plan=${planId}${warRoomQuery}`);
      return;
    }

    // User is already logged in with an active session
    toast.info("Ya tienes una sesión iniciada y un plan activo.", {
      description: "Redirigiendo a tu panel de control...",
    });
    navigate("/dashboard");
  };

  const formatPrice = (price: number, currency: string = "USD") => {
    const isCOP = currency.includes("COP");
    return new Intl.NumberFormat(isCOP ? "es-CO" : "en-US", {
      style: "currency",
      currency: isCOP ? "COP" : "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: isCOP ? 0 : 2,
    }).format(price);
  };

  const calculatePrice = (plan: PlanWithFeatures) => {
    if (planType === "annual") {
      const annualPrice = plan.price_per_month * 12;
      const discountedPrice = annualPrice * 0.8;
      return discountedPrice;
    }
    if (isWarRoomPromo) {
      return Math.round(plan.price_per_month * 0.8);
    }
    return plan.price_per_month;
  };

  if (isLoading) {
    return (
      <section id="pricing" className="py-20 lg:py-24 flex items-center justify-center min-h-[500px] bg-white dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-9 h-9 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Cargando planes oficiales...</p>
        </div>
      </section>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <section id="pricing" className="py-20 lg:py-24 flex items-center justify-center min-h-[500px] bg-white dark:bg-slate-950">
        <p className="text-slate-600">No se pudieron cargar los planes.</p>
      </section>
    );
  }

  const monthlyPlans = plans.filter((plan) => plan.interval === "month");

  return (
    <section id="pricing" className="py-20 lg:py-24 scroll-mt-20 bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[850px] h-[350px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/60 border border-blue-200/80 dark:border-blue-900 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <Zap className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Tarifas Claras y Transparentes</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            Invierte en la productividad de tu{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-400 dark:to-indigo-300">
              práctica jurídica
            </span>
          </h2>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            Elige el plan ideal según el tamaño de tu despacho o volumen de cartera. Sin costos ocultos.
          </p>

          {isWarRoomPromo && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-xs sm:text-sm font-bold shadow-sm">
              <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>Beneficio War Room Legal Academy Activo: 20% de descuento aplicado en tu primer mes (Cupón: WARROOM20)</span>
            </div>
          )}
        </div>

        {/* Pricing Toggle Switch */}
        <div className="flex justify-center mb-12 sm:mb-16">
          <div className="bg-slate-100 dark:bg-slate-900 p-1.5 rounded-full border border-slate-200 dark:border-slate-800 shadow-inner inline-flex items-center gap-1">
            <button
              onClick={() => setPlanType("monthly")}
              className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${
                planType === "monthly"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-600/25"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              Facturación Mensual
            </button>
            <button
              onClick={() => setPlanType("annual")}
              className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                planType === "annual"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-600/25"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <span>Pago Anual</span>
              <span className="text-[10px] uppercase tracking-wider font-extrabold bg-emerald-400 text-slate-950 px-2 py-0.5 rounded-full shadow-sm">
                20% OFF
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto items-stretch">
          {monthlyPlans.map((plan, index) => {
            const isPopular = plan.is_popular;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col p-7 sm:p-8 rounded-3xl transition-all duration-300 ${
                  isPopular
                    ? "bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white border-2 border-blue-500/60 shadow-2xl shadow-blue-600/20 md:-translate-y-2 z-10"
                    : "bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200/90 dark:border-slate-800 shadow-xl shadow-slate-900/5 hover:border-slate-300 hover:-translate-y-1"
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-black uppercase tracking-wider px-4 py-1 rounded-full shadow-lg border border-blue-400/40">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                    <span>Más Elegido por Despachos</span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <h3
                    className={`text-2xl font-black mb-2 ${
                      isPopular ? "text-white" : "text-slate-900 dark:text-white"
                    }`}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={`text-xs sm:text-sm leading-relaxed ${
                      isPopular ? "text-slate-300" : "text-slate-500 dark:text-slate-400"
                    }`}
                  >
                    {plan.description}
                  </p>
                </div>

                {/* Price Display */}
                <div className="mb-6 pb-6 border-b border-slate-100 dark:border-slate-800/80">
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-4xl sm:text-5xl font-black tracking-tight ${
                        isPopular ? "text-white" : "text-slate-900 dark:text-white"
                      }`}
                    >
                      {formatPrice(calculatePrice(plan), plan.currency)}
                    </span>
                    <span
                      className={`text-xs sm:text-sm font-semibold ${
                        isPopular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      /{planType === "monthly" ? "mes" : "año"}
                    </span>
                  </div>

                  {planType === "annual" && (
                    <div className="mt-2 text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span>
                        Ahorras{" "}
                        {formatPrice(
                          plan.price_per_month * 12 - calculatePrice(plan),
                          plan.currency
                        )}{" "}
                        al año
                      </span>
                    </div>
                  )}
                </div>

                {/* Feature List */}
                <div className="space-y-3.5 mb-8 flex-grow">
                  <span
                    className={`text-[11px] font-bold uppercase tracking-wider block mb-2 ${
                      isPopular ? "text-blue-400" : "text-slate-400"
                    }`}
                  >
                    ¿Qué incluye este plan?
                  </span>

                  {plan.features.map((feature) => (
                    <div key={feature.id} className="flex items-start gap-2.5">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          isPopular
                            ? "bg-blue-500/20 text-blue-300 border border-blue-400/30"
                            : "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                        }`}
                      >
                        <Check size={12} strokeWidth={3} />
                      </div>
                      <span
                        className={`text-xs sm:text-sm leading-snug font-medium ${
                          isPopular ? "text-slate-200" : "text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleStartTrial(plan.id)}
                  disabled={isStartingTrial}
                  className={`w-full rounded-xl h-12 text-sm sm:text-base font-extrabold transition-all duration-300 flex items-center justify-center gap-2 ${
                    isPopular
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-xl shadow-blue-600/30 hover:scale-[1.02]"
                      : "bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 shadow-md hover:scale-[1.02]"
                  }`}
                >
                  {isStartingTrial ? (
                    "Iniciando prueba..."
                  ) : (
                    <>
                      <span>Comenzar Prueba Gratuita</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Trust & Guarantee Strip */}
        <div className="mt-14 pt-8 border-t border-slate-200/80 dark:border-slate-800 flex flex-wrap items-center justify-center gap-8 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>14 días de prueba gratuita sin compromiso</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-blue-600" />
            <span>Sin permanencia mínima, cancela en cualquier momento</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-indigo-600" />
            <span>Facturación electrónica oficial válida ante la DIAN</span>
          </div>
        </div>

      </div>
    </section>
  );
}
